import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import TextType from '../components/TextType';
import { getFeaturedProjects } from '../data/projectsData';
import { 
  Linkedin, 
  Github, 
  Mail, 
  Download,
  Sparkles,
  Database,
  TrendingUp,
  Users,
  ArrowRight,
  Eye
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [showResume, setShowResume] = useState(false);
  const featuredProjects = getFeaturedProjects();

  const resumeUrl = '/resume/Pradeepta_Dey_Resume.pdf';

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white">
      {/* Profile Image Section */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto flex justify-center">
          <div className="w-64 h-64 rounded-full overflow-hidden border-4 border-gray-200 dark:border-gray-800 shadow-2xl">
            <img
              src="https://customer-assets.emergentagent.com/job_dc30b116-dcfb-43c4-b9b7-5db372078b51/artifacts/99lyctnp_Gemini_Generated_Image_s7qx9hs7qx9hs7qx.png"
              alt="Pradeepta Dey"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="flex items-center justify-center px-6 py-12">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-6xl md:text-7xl font-light tracking-tight">
              Hi, I'm Pradeepta! 👋
            </h1>
            <div className="text-2xl md:text-3xl font-light text-gray-600 dark:text-gray-400 h-12">
              <TextType
                text={['Data Analyst', 'AI/ML Enthusiast', 'Product Lead']}
                typingSpeed={100}
                deletingSpeed={50}
                pauseDuration={2000}
                showCursor={true}
                cursorCharacter="|"
                loop={true}
              />
            </div>
          </div>

          {/* Learn More Button */}
          <div className="pt-4">
            <Button 
              onClick={() => navigate('/about')}
              variant="outline"
              className="gap-2 px-6 py-6 text-base border-2 hover:bg-gray-50 dark:hover:bg-gray-900"
            >
              Learn more about me
              <ArrowRight size={18} />
            </Button>
          </div>

          {/* Social Links */}
          <div className="flex items-center justify-center gap-4 pt-4">
            <a 
              href="https://www.linkedin.com/in/pradeeptadey/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin size={22} />
            </a>
            <a 
              href="https://github.com/evilNami" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
              aria-label="GitHub"
            >
              <Github size={22} />
            </a>
            <a 
              href="mailto:pradeeptadey2601@gmail.com"
              className="p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
              aria-label="Email"
            >
              <Mail size={22} />
            </a>
          </div>

          {/* Resume Buttons */}
          <div className="flex items-center justify-center gap-4 pt-8">
            <Dialog open={showResume} onOpenChange={setShowResume}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 px-6 py-6 text-base">
                  <Eye size={18} />
                  Preview Resume
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl h-[90vh]">
                <DialogHeader>
                  <DialogTitle>Resume Preview</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-hidden">
                  <iframe
                    src={resumeUrl}
                    className="w-full h-full border-0"
                    title="Resume Preview"
                  />
                </div>
              </DialogContent>
            </Dialog>

            <a 
              href={resumeUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="gap-2 px-6 py-6 text-base">
                <Download size={18} />
                Download Resume
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Small Intro About Me */}
      <section className="py-20 px-6 bg-gray-50 dark:bg-gray-950">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight">About Me</h2>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            After starting my career in Software engineering, I discovered my passion lies not in <span className="text-black dark:text-white font-medium">how we build</span>, but in <span className="text-black dark:text-white font-medium">what we build and why</span>. Currently finishing my MS, I am leading the 0-to-1 product development of OharaLabs AI—handling product strategy, user research, and shipping 'Albert', our proprietary AI tutor.
          </p>
          <Button 
            onClick={() => navigate('/about')}
            variant="outline"
            className="gap-2 mt-4"
          >
            Read Full Story
            <ArrowRight size={16} />
          </Button>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-4">Featured Projects</h2>
            <p className="text-gray-600 dark:text-gray-400">Some of my recent work</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {featuredProjects.slice(0, 3).map((project, index) => {
              const iconMap = {
                'Machine Learning': Sparkles,
                'Big Data': Database,
                'Predictive Analytics': TrendingUp,
                'Business Intelligence': Users
              };
              const Icon = iconMap[project.category] || Sparkles;
              
              return (
                <Card 
                  key={index} 
                  className="group overflow-hidden border border-gray-200 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600 transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(`/projects/${project.slug}`)}
                >
                  <div className="relative h-48 overflow-hidden bg-gray-100 dark:bg-gray-900">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <CardHeader>
                    <div className="flex items-start gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-900">
                        <Icon size={20} />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-lg font-normal mb-2">{project.title}</CardTitle>
                        <Badge variant="secondary" className="text-xs font-normal">{project.category}</Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {project.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/projects')}
              className="gap-2"
            >
              View All Projects
              <ArrowRight size={16} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;